/**
 * Policy Integration for agentshield-nextjs
 *
 * This module provides policy evaluation support for the Next.js middleware.
 * It can use:
 * - Local policy configuration (static)
 * - Fetched policy from AgentShield API (dynamic with caching)
 * - Fallback/default policies
 *
 * @example
 * ```typescript
 * import { createPolicyMiddleware } from '@kya-os/agentshield-nextjs/policy';
 *
 * export default createPolicyMiddleware({
 *   policy: {
 *     enabled: true,
 *     defaultAction: 'allow',
 *     thresholds: { confidenceThreshold: 80, confidenceAction: 'block' },
 *     allowList: [{ clientName: 'ChatGPT' }],
 *   },
 * });
 * ```
 */

import { NextRequest, NextResponse } from 'next/server';
import {
  evaluatePolicy,
  createEvaluationContext,
  createPolicyFetcher,
  matchPath,
  PolicyFetcher,
  PolicyConfigSchema,
  ENFORCEMENT_ACTIONS,
  DEFAULT_POLICY,
  type PolicyConfig,
  type PolicyEvaluationContext,
  type PolicyEvaluationResult,
  type PolicyFetcherConfig,
  type DetectionResult,
} from '@kya-os/agentshield-shared';

// Re-export shared policy types for convenience
export {
  evaluatePolicy,
  createEvaluationContext,
  type PolicyConfig,
  type PolicyEvaluationContext,
  type PolicyEvaluationResult,
  ENFORCEMENT_ACTIONS,
  DEFAULT_POLICY,
} from '@kya-os/agentshield-shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Policy middleware configuration
 */
export interface PolicyMiddlewareConfig {
  /**
   * Local policy configuration (static)
   * If provided, this policy is used instead of fetching from API
   */
  policy?: Partial<PolicyConfig>;

  /**
   * Fetch policy from AgentShield API
   * Requires projectId and optionally an apiKey
   */
  fetchPolicy?: {
    /** Project ID to fetch policy for */
    projectId: string;
    /** API base URL (defaults to production) */
    apiUrl?: string;
    /** API key for authentication */
    apiKey?: string;
    /** Cache TTL in seconds (default: 300) */
    cacheTtlSeconds?: number;
  };

  /**
   * Fallback policy to use when fetch fails
   * Defaults to DEFAULT_POLICY (allow all)
   */
  fallbackPolicy?: Partial<PolicyConfig>;

  /**
   * Custom blocked response
   */
  blockedResponse?: {
    status?: number;
    message?: string;
    headers?: Record<string, string>;
  };

  /**
   * Default redirect URL for redirect actions
   */
  redirectUrl?: string;

  /**
   * Callback when policy decision is made
   */
  onPolicyDecision?: (
    request: NextRequest,
    decision: PolicyEvaluationResult,
    context: PolicyEvaluationContext
  ) => void | Promise<void>;

  /**
   * Custom response builder for blocked requests
   */
  customBlockedResponse?: (
    request: NextRequest,
    decision: PolicyEvaluationResult
  ) => NextResponse | Promise<NextResponse>;

  /**
   * Whether to fail open (allow) on policy evaluation errors
   * Default: true (recommended for production)
   */
  failOpen?: boolean;

  /**
   * Enable debug logging
   */
  debug?: boolean;
}

/**
 * Combined middleware configuration with policy support
 */
export interface NextJSPolicyMiddlewareConfig extends PolicyMiddlewareConfig {
  /**
   * Paths to skip (in addition to policy excludedPaths)
   */
  skipPaths?: string[];

  /**
   * Only enforce on these paths (overrides policy includedPaths)
   */
  includePaths?: string[];
}

// ============================================================================
// Policy Evaluation Helper
// ============================================================================

/**
 * Create policy evaluation context from detection result and request
 */
export function createContextFromDetection(
  detection: DetectionResult,
  request: NextRequest
): PolicyEvaluationContext {
  return createEvaluationContext({
    agentType: detection.detectedAgent?.type,
    agentName: detection.detectedAgent?.name,
    agentVendor: detection.detectedAgent?.vendor,
    confidence: detection.confidence,
    riskLevel: detection.riskLevel,
    path: request.nextUrl.pathname,
    method: request.method,
    signatureVerified: detection.verificationMethod === 'signature',
    isAuthenticated: false, // TODO: integrate with auth
    userAgent: request.headers.get('user-agent') || undefined,
  });
}

/**
 * Evaluate policy for a detection result
 */
export function evaluatePolicyForDetection(
  detection: DetectionResult,
  request: NextRequest,
  policy: PolicyConfig
): PolicyEvaluationResult {
  const context = createContextFromDetection(detection, request);
  return evaluatePolicy(policy, context);
}

// ============================================================================
// Response Builders
// ============================================================================

/**
 * Build blocked response based on policy decision
 */
export function buildBlockedResponse(
  decision: PolicyEvaluationResult,
  config: PolicyMiddlewareConfig
): NextResponse {
  const status = config.blockedResponse?.status ?? 403;
  const message = config.blockedResponse?.message ?? decision.message ?? 'Access denied';

  const response = NextResponse.json(
    {
      error: message,
      code: 'POLICY_BLOCKED',
      reason: decision.reason,
      ruleId: decision.ruleId,
      matchType: decision.matchType,
    },
    { status }
  );

  // Add custom headers
  if (config.blockedResponse?.headers) {
    for (const [key, value] of Object.entries(config.blockedResponse.headers)) {
      response.headers.set(key, value);
    }
  }

  // Add AgentShield headers
  response.headers.set('X-AgentShield-Action', decision.action);
  response.headers.set('X-AgentShield-Reason', decision.reason);
  response.headers.set('X-AgentShield-MatchType', decision.matchType);

  return response;
}

/**
 * Build redirect response based on policy decision
 */
export function buildRedirectResponse(
  request: NextRequest,
  decision: PolicyEvaluationResult,
  config: PolicyMiddlewareConfig
): NextResponse {
  const redirectUrl = decision.redirectUrl || config.redirectUrl || '/blocked';
  const url = new URL(redirectUrl, request.url);

  // Add query params with policy info
  url.searchParams.set('reason', decision.reason);
  if (decision.ruleId) {
    url.searchParams.set('ruleId', decision.ruleId);
  }

  return NextResponse.redirect(url);
}

/**
 * Build challenge response (placeholder - future implementation)
 */
export function buildChallengeResponse(
  request: NextRequest,
  decision: PolicyEvaluationResult,
  config: PolicyMiddlewareConfig
): NextResponse {
  // For now, treat challenge as redirect
  // Future: implement CAPTCHA, proof-of-work, etc.
  return buildRedirectResponse(request, decision, config);
}

// ============================================================================
// Policy Handler
// ============================================================================

/**
 * Handle policy decision and return appropriate response
 */
export async function handlePolicyDecision(
  request: NextRequest,
  decision: PolicyEvaluationResult,
  config: PolicyMiddlewareConfig
): Promise<NextResponse | null> {
  switch (decision.action) {
    case ENFORCEMENT_ACTIONS.BLOCK:
      if (config.customBlockedResponse) {
        return await config.customBlockedResponse(request, decision);
      }
      return buildBlockedResponse(decision, config);

    case ENFORCEMENT_ACTIONS.REDIRECT:
      return buildRedirectResponse(request, decision, config);

    case ENFORCEMENT_ACTIONS.CHALLENGE:
      return buildChallengeResponse(request, decision, config);

    case ENFORCEMENT_ACTIONS.LOG:
      // LOG action always logs - that's its purpose
      // (debug flag controls verbose debugging output, not LOG action behavior)
      console.log('[AgentShield] Policy decision (log):', {
        path: request.nextUrl.pathname,
        action: decision.action,
        reason: decision.reason,
        matchType: decision.matchType,
        ruleId: decision.ruleId,
      });
      return null; // Continue to allow

    case ENFORCEMENT_ACTIONS.ALLOW:
    default:
      return null; // Continue
  }
}

// ============================================================================
// Policy Fetcher Integration
// ============================================================================

// Cache fetchers by config to avoid recreating them, but also support
// different configurations (different apiUrl, apiKey, etc.)
const fetcherCache = new Map<string, PolicyFetcher>();

/**
 * Generate a cache key for fetcher config.
 * Uses ?? to distinguish between explicit 0 and undefined values.
 */
function getFetcherCacheKey(config: NonNullable<PolicyMiddlewareConfig['fetchPolicy']>): string {
  return `${config.apiUrl ?? 'default'}:${config.apiKey ?? ''}:${config.cacheTtlSeconds ?? 'default'}`;
}

/**
 * Get or create policy fetcher for the given config
 */
function getPolicyFetcher(config: PolicyMiddlewareConfig['fetchPolicy']): PolicyFetcher {
  if (!config) {
    throw new Error('fetchPolicy config required');
  }

  const cacheKey = getFetcherCacheKey(config);
  let fetcher = fetcherCache.get(cacheKey);

  if (!fetcher) {
    const fetcherConfig: PolicyFetcherConfig = {
      apiBaseUrl: config.apiUrl || 'https://kya.vouched.id',
      apiKey: config.apiKey,
      cacheTtlSeconds: config.cacheTtlSeconds,
    };
    fetcher = createPolicyFetcher(fetcherConfig);
    fetcherCache.set(cacheKey, fetcher);
  }

  return fetcher;
}

/**
 * Get policy (local, fetched, or fallback)
 */
export async function getPolicy(config: PolicyMiddlewareConfig): Promise<PolicyConfig> {
  // Use local policy if provided
  if (config.policy) {
    return PolicyConfigSchema.parse({ ...DEFAULT_POLICY, ...config.policy });
  }

  // Fetch from API if configured
  if (config.fetchPolicy) {
    try {
      const fetcher = getPolicyFetcher(config.fetchPolicy);
      return await fetcher.getPolicy(config.fetchPolicy.projectId);
    } catch (error) {
      if (config.debug) {
        console.warn('[AgentShield] Policy fetch failed, using fallback:', error);
      }
      // Return fallback policy
      return PolicyConfigSchema.parse({
        ...DEFAULT_POLICY,
        ...(config.fallbackPolicy || {}),
      });
    }
  }

  // No policy configured - return default (allow all)
  return PolicyConfigSchema.parse(DEFAULT_POLICY);
}

// ============================================================================
// Standalone Policy Middleware
// ============================================================================

/**
 * Apply policy to a detection result
 *
 * This function can be used standalone to evaluate policy after detection.
 * Supports extended config with skipPaths and includePaths for path-based filtering.
 *
 * @example
 * ```typescript
 * const result = await detector.analyze(context);
 * const response = await applyPolicy(request, result, {
 *   policy: { thresholds: { confidenceThreshold: 80 } },
 *   skipPaths: ['/health', '/api/public/*'],
 *   includePaths: ['/api/*'],
 * });
 *
 * if (response) {
 *   return response; // Policy blocked the request
 * }
 * ```
 */
export async function applyPolicy(
  request: NextRequest,
  detection: DetectionResult,
  config: NextJSPolicyMiddlewareConfig
): Promise<NextResponse | null> {
  try {
    const path = request.nextUrl.pathname;

    // Check skipPaths - if path matches any skip pattern, allow through
    if (config.skipPaths?.some((pattern) => matchPath(path, pattern))) {
      return null; // Skip policy enforcement for this path
    }

    // Check includePaths - if defined, path must match at least one pattern
    if (config.includePaths && config.includePaths.length > 0) {
      if (!config.includePaths.some((pattern) => matchPath(path, pattern))) {
        return null; // Path not in included paths, skip policy enforcement
      }
    }

    // Get policy
    const policy = await getPolicy(config);

    // Create context and evaluate
    const context = createContextFromDetection(detection, request);
    const decision = evaluatePolicy(policy, context);

    // Call decision callback if provided
    if (config.onPolicyDecision) {
      await config.onPolicyDecision(request, decision, context);
    }

    // Handle decision
    return await handlePolicyDecision(request, decision, config);
  } catch (error) {
    if (config.debug) {
      console.error('[AgentShield] Policy evaluation error:', error);
    }

    if (config.failOpen !== false) {
      return null; // Allow on error
    }

    // Fail closed
    return NextResponse.json(
      { error: 'Security check failed', code: 'POLICY_ERROR' },
      { status: 503 }
    );
  }
}
